class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Upsell "ADD" buttons — delegated so it survives drawer re-renders.
    this.addEventListener('click', (event) => {
      const upgradeButton = event.target.closest('[data-cart-upgrade]');
      if (!upgradeButton) return;
      event.preventDefault();
      this.swapPack(upgradeButton, {
        button: upgradeButton,
        busyLabel: upgradeButton.dataset.busy || 'Adding…',
      });
    });

    // Tier-swapping stepper: on a pack line, "+" jumps to the next pack and "−"
    // to the previous, instead of changing quantity. Captured on the way DOWN so
    // this runs before Dawn's QuantityInput handler (which lives on the button),
    // letting us cancel the native step. Buttons without the data attributes fall
    // through to normal quantity behavior (e.g. plus on a 3-pack, minus on a 1-pack).
    this.addEventListener(
      'click',
      (event) => {
        const stepButton = event.target.closest('[data-tgt-id],[data-pack-add]');
        if (!stepButton || stepButton.hasAttribute('disabled')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (stepButton.dataset.tgtId) {
          this.swapPack(stepButton, { trigger: stepButton });
        } else if (stepButton.dataset.packAdd) {
          // "+" on the top pack (3-pack) adds another single bottle line so the
          // order can grow past three.
          this.addPack(stepButton.dataset.packAdd, { trigger: stepButton });
        }
      },
      true
    );

    setTimeout(() => this.setShipFill(), 0);
  }

  // Force the free-shipping fill width with !important — the cart page's bar
  // needs this because a theme rule overrides a plain width, so we match it.
  setShipFill() {
    const fill = this.querySelector('.atd-progress__fill');
    if (fill && fill.dataset.pct != null) {
      // display:block also overrides the theme's `div:empty { display:none }`.
      fill.style.setProperty('display', 'block', 'important');
      fill.style.setProperty('width', fill.dataset.pct + '%', 'important');
    }
  }

  // Swap a specific pack line to another tier. We remove that exact line (by its
  // line-item key) and add the target variant — add.js increments an existing
  // matching line, so building a 2nd 3-pack (or any duplicate) works, which a
  // variant-keyed /cart/update.js can't do.
  // Swap a pack line to another tier. The button carries the exact absolute
  // quantities to set (computed in Liquid), so update.js merges with an existing
  // pack of the target tier (e.g. two 3-packs) instead of overwriting it.
  swapPack(button, opts = {}) {
    const srcId = button.dataset.srcId;
    const tgtId = button.dataset.tgtId;
    if (!srcId || !tgtId) return;
    const updates = {};
    updates[srcId] = parseInt(button.dataset.srcQty, 10);
    updates[tgtId] = parseInt(button.dataset.tgtQty, 10);
    this._runSteps([{ url: routes.cart_update_url, payload: { updates } }], opts);
  }

  // Add one more single-bottle (1-pack) line via /cart/add.js.
  addPack(id, opts = {}) {
    if (!id) return;
    this._runSteps([{ url: routes.cart_add_url, payload: { items: [{ id: id, quantity: 1 }] } }], opts);
  }

  // Run cart mutations in sequence; only the last carries `sections` and drives
  // the in-place re-render of the drawer + cart bubble.
  _runSteps(steps, opts = {}) {
    const { button, busyLabel, trigger } = opts;

    const errorsEl = document.getElementById('CartDrawer-CartErrors');
    if (errorsEl) errorsEl.textContent = '';

    let originalLabel;
    if (button) {
      originalLabel = button.textContent;
      button.setAttribute('disabled', 'disabled');
      button.setAttribute('aria-busy', 'true');
      if (busyLabel) button.textContent = busyLabel;
    }
    const inner = this.querySelector('.drawer__inner');
    if (inner) inner.classList.add('atd-swapping');
    if (trigger) trigger.classList.add('is-busy');

    const run = (i) => {
      const step = steps[i];
      const isLast = i === steps.length - 1;
      const payload = isLast
        ? { ...step.payload, sections: 'cart-drawer,cart-icon-bubble', sections_url: window.location.pathname }
        : step.payload;

      return fetch(step.url, { ...fetchConfig(), ...{ body: JSON.stringify(payload) } })
        .then((response) => response.json())
        .then((parsedState) => {
          if (parsedState.status) throw parsedState;
          if (!isLast) return run(i + 1);
          if (!parsedState.sections) throw new Error('no sections in response');

          const newInner = new DOMParser()
            .parseFromString(parsedState.sections['cart-drawer'], 'text/html')
            .querySelector('.drawer__inner');
          const currentInner = this.querySelector('.drawer__inner');
          if (newInner && currentInner) {
            currentInner.replaceWith(newInner);
          }
          this.setShipFill();

          const bubble = document.getElementById('cart-icon-bubble');
          if (bubble && parsedState.sections['cart-icon-bubble']) {
            const newBubble = new DOMParser()
              .parseFromString(parsedState.sections['cart-icon-bubble'], 'text/html')
              .querySelector('.shopify-section');
            if (newBubble) bubble.innerHTML = newBubble.innerHTML;
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState });
          }
          return null;
        });
    };

    run(0).catch((e) => {
      console.error('[cart-drawer] cart mutation error', e);
      this.abortSwap(inner, button, originalLabel);
    });
  }

  abortSwap(inner, button, label) {
    if (inner) inner.classList.remove('atd-swapping');
    if (button) {
      button.removeAttribute('disabled');
      button.removeAttribute('aria-busy');
      if (label !== undefined) button.textContent = label;
    }
    const errors = document.getElementById('CartDrawer-CartErrors');
    if (errors && window.cartStrings) errors.textContent = window.cartStrings.error;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.setShipFill();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Clear the empty-cart state on BOTH the wrapper and inner — otherwise a
    // drawer that was empty (page load or after removing items) opens in its
    // centered empty-state layout (hidden header/footer) even with items in it.
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
